// admin-web/src/pages/NewsPage.tsx
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { db, functions, storage } from "../firebase";

type Attachment = {
  id: string; // uuid-ish
  name: string;
  size: number;
  contentType: string;
  storagePath: string;
  downloadUrl?: string;
  createdAtIso: string;
};

type NewsDoc = {
  id: string;
  title: string;
  body: string;
  summary?: string;
  schoolYear?: string;

  status: string;
  authorUid: string;

  // requests from staff
  pushRequested?: boolean;
  pinnedRequested?: boolean;
  urgentRequested?: boolean;

  // admin-approved flags
  pushApproved?: boolean;
  pinned?: boolean;
  urgent?: boolean;

  // scheduling + expiry
  scheduledPublishAt?: any; // Timestamp|null
  expiresAt?: any; // Timestamp
  publishedAt?: any; // Timestamp|null
  submittedAt?: any;
  updatedAt?: any;

  // published edit audit
  editedAt?: any;
  editedByUid?: string;

  // moderation
  rejectionReason?: string;

  // soft delete
  deletedAt?: any;
  deletedByUid?: string;

  // attachments
  attachments?: Attachment[];
};

type AdminNewsTab = "mine" | "all" | "pending";

type AttachmentPolicy = {
  allowedContentTypes: string[];
  maxBytesNews: number;
  maxFilesPerNewsPost: number;
};

const DEFAULT_ATTACHMENT_POLICY: AttachmentPolicy = {
  allowedContentTypes: [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/webp",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
  ],
  maxBytesNews: 5 * 1024 * 1024,
  maxFilesPerNewsPost: 3,
};

function toMillisSafe(v: any) {
  try {
    if (!v) return 0;
    if (typeof v?.toMillis === "function") return v.toMillis();
    const d = new Date(v);
    const t = d.getTime();
    return Number.isFinite(t) ? t : 0;
  } catch {
    return 0;
  }
}

function getCallableErrorMessage(e: any): string {
  const code = e?.code ? String(e.code) : "";
  const msg = e?.message ? String(e.message) : "Unknown error";
  return code ? `${code}: ${msg}` : msg;
}

function isoNowPlusDays(days: number) {
  const ms = Date.now() + days * 24 * 60 * 60 * 1000;
  return new Date(ms).toISOString();
}

function isoToDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function datetimeLocalToIso(dtLocal: string): string | null {
  if (!dtLocal) return null;
  const d = new Date(dtLocal);
  const t = d.getTime();
  if (!Number.isFinite(t)) return null;
  return d.toISOString();
}

function makeId() {
  return Math.random().toString(16).slice(2) + "-" + Date.now().toString(16);
}

function adjacentSchoolYear(label: string, offset: number) {
  const match = label.trim().match(/^(\d{4})-(\d{4})$/);
  if (!match) return "";
  const start = Number(match[1]) + offset;
  return `${start}-${start + 1}`;
}

function publicNewsUrl() {
  return "https://zionclassical.org/news";
}

function facebookReadyCopy(item: NewsDoc) {
  return [
    item.title,
    "",
    item.summary || item.body?.slice(0, 280) || "Read the latest update from Zion Classical Academy.",
    "",
    `Read more: ${publicNewsUrl()}`,
  ].join("\n");
}

export function NewsPage() {
  const { user, isAdmin, isStaff, isSuperAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [myItems, setMyItems] = useState<NewsDoc[]>([]);
  const [pending, setPending] = useState<NewsDoc[]>([]);
  const [allItems, setAllItems] = useState<NewsDoc[]>([]);
  const [error, setError] = useState<string | null>(null);

  // editor state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingStatus, setEditingStatus] = useState<string | null>(null); // draft/rejected/published/etc
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [body, setBody] = useState("");
  const [schoolYear, setSchoolYear] = useState("");
  const [activeSchoolYear, setActiveSchoolYear] = useState("");
  const [previousSchoolYear, setPreviousSchoolYear] = useState("");

  const [pushRequested, setPushRequested] = useState(false);
  const [pinnedRequested, setPinnedRequested] = useState(false);
  const [urgentRequested, setUrgentRequested] = useState(false);

  // required expiry + optional schedule (local inputs)
  const [expiresAtLocal, setExpiresAtLocal] = useState<string>(""); // datetime-local
  const [scheduledPublishAtLocal, setScheduledPublishAtLocal] = useState<string>(""); // datetime-local

  const [draftAttachments, setDraftAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);

  // approval panel state
  const [approvePush, setApprovePush] = useState(true);
  const [approvePinned, setApprovePinned] = useState(false);
  const [approveUrgent, setApproveUrgent] = useState(false);
  const [approveScheduleLocal, setApproveScheduleLocal] = useState<string>("");
  const [approveExpiresLocal, setApproveExpiresLocal] = useState<string>("");

  // defaults from settings/newsPolicy (optional)
  const [defaultExpireDays, setDefaultExpireDays] = useState<number>(7);
  const [attachmentPolicy, setAttachmentPolicy] = useState<AttachmentPolicy>(DEFAULT_ATTACHMENT_POLICY);

  // UI toggles
  const [showDeleted, setShowDeleted] = useState(false); // admin can toggle
  const [adminTab, setAdminTab] = useState<AdminNewsTab>("mine");

  const upsertDraft = useMemo(() => httpsCallable(functions, "upsertNewsDraft"), []);
  const submitItem = useMemo(() => httpsCallable(functions, "submitNewsItem"), []);
  const approveItem = useMemo(() => httpsCallable(functions, "approveNewsItem"), []);
  const rejectItem = useMemo(() => httpsCallable(functions, "rejectNewsItem"), []);
  const archiveItem = useMemo(() => httpsCallable(functions, "archiveNewsItem"), []);

  async function loadPolicy() {
    try {
      const snap = await getDoc(doc(db, "settings", "newsPolicy"));
      if (snap.exists()) {
        const d = snap.data() as any;
        const days = Number(d?.defaultExpireDays);
        if (Number.isFinite(days) && days >= 1 && days <= 365) setDefaultExpireDays(days);
      }
    } catch {
      // ignore
    }
  }

  async function loadAttachmentPolicy() {
    try {
      const snap = await getDoc(doc(db, "settings", "attachmentPolicy"));
      if (!snap.exists()) {
        setAttachmentPolicy(DEFAULT_ATTACHMENT_POLICY);
        return;
      }
      const d = snap.data() as any;
      const maxBytesNews = Number(d.maxBytesNews ?? DEFAULT_ATTACHMENT_POLICY.maxBytesNews);
      const maxFilesPerNewsPost = Number(d.maxFilesPerNewsPost ?? DEFAULT_ATTACHMENT_POLICY.maxFilesPerNewsPost);
      const storedAllowed = Array.isArray(d.allowedMimeTypes) && d.allowedMimeTypes.length
        ? d.allowedMimeTypes.map(String)
        : Array.isArray(d.allowedContentTypes) && d.allowedContentTypes.length
        ? d.allowedContentTypes.map(String)
        : DEFAULT_ATTACHMENT_POLICY.allowedContentTypes;
      setAttachmentPolicy({
        allowedContentTypes: Array.from(new Set([...storedAllowed, ...DEFAULT_ATTACHMENT_POLICY.allowedContentTypes])),
        maxBytesNews: Number.isFinite(maxBytesNews) && maxBytesNews > 0 ? maxBytesNews : DEFAULT_ATTACHMENT_POLICY.maxBytesNews,
        maxFilesPerNewsPost: Number.isFinite(maxFilesPerNewsPost) && maxFilesPerNewsPost > 0 ? maxFilesPerNewsPost : DEFAULT_ATTACHMENT_POLICY.maxFilesPerNewsPost,
      });
    } catch {
      setAttachmentPolicy(DEFAULT_ATTACHMENT_POLICY);
    }
  }

  async function loadSchoolYearSettings() {
    try {
      const snap = await getDoc(doc(db, "settings", "schoolYear"));
      const d = snap.exists() ? (snap.data() as any) : {};
      const active = typeof d.activeYear === "string" ? d.activeYear.trim() : "";
      const previous = typeof d.previousYear === "string" ? d.previousYear.trim() : "";
      setActiveSchoolYear(active);
      setPreviousSchoolYear(previous);
      if (!schoolYear && active) setSchoolYear(active);
    } catch {
      // ignore
    }
  }

  async function reload() {
    if (!user) return;
    setLoading(true);
    setError(null);

    try {
      await loadAttachmentPolicy();
      // My items
      const myQ = query(
        collection(db, "newsItems"),
        where("authorUid", "==", user.uid),
        orderBy("updatedAt", "desc")
      );
      const mySnap = await getDocs(myQ);
      let mine = mySnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as NewsDoc[];

      if (!showDeleted) mine = mine.filter((x) => x.status !== "deleted");
      setMyItems(mine);

      if (isAdmin || isSuperAdmin) {
        // All items for admin/super admin
        const allQ = query(
          collection(db, "newsItems"),
          orderBy("updatedAt", "desc")
        );
        const allSnap = await getDocs(allQ);
        let all = allSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as NewsDoc[];

        if (!showDeleted) all = all.filter((x) => x.status !== "deleted");
        setAllItems(all);

        // Pending queue
        const pendingQ = query(
          collection(db, "newsItems"),
          where("status", "==", "pending"),
          orderBy("submittedAt", "desc")
        );
        const pSnap = await getDocs(pendingQ);
        setPending(pSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as NewsDoc[]);
      } else {
        setPending([]);
        setAllItems([]);
      }
    } catch (e: any) {
      console.error("[NewsPage] reload failed", e);
      setError(getCallableErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!user) return;
    Promise.all([loadPolicy(), loadSchoolYearSettings()]).finally(() => reload());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, isAdmin, isSuperAdmin, showDeleted]);

  function applyDefaultExpiryIfMissing() {
    if (expiresAtLocal) return;
    const defaultExpIso = isoNowPlusDays(defaultExpireDays);
    setExpiresAtLocal(isoToDatetimeLocal(defaultExpIso));
  }

  function resetEditor() {
    setEditingId(null);
    setEditingStatus(null);
    setTitle("");
    setSummary("");
    setBody("");
    setSchoolYear(activeSchoolYear);
    setPushRequested(false);
    setPinnedRequested(false);
    setUrgentRequested(false);

    const defaultExpIso = isoNowPlusDays(defaultExpireDays);
    setExpiresAtLocal(isoToDatetimeLocal(defaultExpIso));

    setScheduledPublishAtLocal("");
    setDraftAttachments([]);
    setError(null);
  }

  function beginNew() {
    resetEditor();
  }

  function beginEdit(item: NewsDoc) {
    setEditingId(item.id);
    setEditingStatus(item.status ?? null);

    setTitle(item.title ?? "");
    setSummary(item.summary ?? "");
    setBody(item.body ?? "");
    setSchoolYear(item.schoolYear ?? activeSchoolYear);

    setPushRequested(item.pushRequested === true);
    setPinnedRequested(item.pinnedRequested === true);
    setUrgentRequested(item.urgentRequested === true);

    const expMs = toMillisSafe(item.expiresAt);
    const expIso = expMs ? new Date(expMs).toISOString() : isoNowPlusDays(defaultExpireDays);
    setExpiresAtLocal(isoToDatetimeLocal(expIso));

    const schMs = toMillisSafe(item.scheduledPublishAt);
    setScheduledPublishAtLocal(schMs ? isoToDatetimeLocal(new Date(schMs).toISOString()) : "");

    setDraftAttachments(Array.isArray(item.attachments) ? item.attachments : []);
    setError(null);
  }

  async function duplicateToDraft(item: NewsDoc) {
    setError(null);
    try {
      if (!user) return;

      const defaultExpIso = isoNowPlusDays(defaultExpireDays);
      const newDoc: any = {
        title: item.title ?? "",
        summary: item.summary ?? "",
        body: item.body ?? "",
        status: "draft",
        authorUid: user.uid,
        pushRequested: item.pushRequested === true,
        pushApproved: false,
        pushSentAt: null,
        pinnedRequested: item.pinnedRequested === true,
        urgentRequested: item.urgentRequested === true,
        pinned: false,
        urgent: false,
        urgentUntil: null,
        scheduledPublishAt: null,
        expiresAt: new Date(defaultExpIso),
        schoolYear: item.schoolYear ?? activeSchoolYear,
        attachments: Array.isArray(item.attachments) ? item.attachments : [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const refDoc = await addDoc(collection(db, "newsItems"), newDoc);
      await reload();
      alert("Duplicated to a new draft.");

      const snap = await getDoc(refDoc);
      if (snap.exists()) beginEdit({ id: snap.id, ...(snap.data() as any) });
    } catch (e: any) {
      console.error("[NewsPage] duplicate failed", e);
      const msg = getCallableErrorMessage(e);
      setError(msg);
      alert(`Duplicate failed:\n${msg}`);
    }
  }

  async function saveDraft(): Promise<string | null> {
    setError(null);
    try {
      applyDefaultExpiryIfMissing();

      if (!expiresAtLocal) {
        alert("Expires At is required.");
        return null;
      }
      const expiresAtIso = datetimeLocalToIso(expiresAtLocal);
      if (!expiresAtIso) {
        alert("Expires At is invalid.");
        return null;
      }

      const scheduledPublishAtIso = scheduledPublishAtLocal
        ? datetimeLocalToIso(scheduledPublishAtLocal)
        : null;

      const res: any = await upsertDraft({
        id: editingId ?? undefined,
        title,
        summary,
        body,
        schoolYear: schoolYear || activeSchoolYear || undefined,
        pushRequested,
        pinnedRequested,
        urgentRequested,
        scheduledPublishAtIso,
        expiresAtIso,
      });

      const id = res?.data?.id ?? editingId;
      if (!id) throw new Error("No draft id returned.");

      await updateDoc(doc(db, "newsItems", id), {
        attachments: draftAttachments,
        updatedAt: serverTimestamp(),
      });

      setEditingId(String(id));
      setEditingStatus("draft");
      await reload();
      alert("Draft saved.");
      return String(id);
    } catch (e: any) {
      console.error("[NewsPage] saveDraft failed", e);
      const msg = getCallableErrorMessage(e);
      setError(msg);
      alert(`Save draft failed:\n${msg}`);
      return null;
    }
  }

  async function savePublishedEdit(): Promise<void> {
    if (!user) return;
    if (!editingId) return;

    setError(null);

    try {
      await updateDoc(doc(db, "newsItems", editingId), {
        title,
        summary,
        body,
        schoolYear: schoolYear || activeSchoolYear || null,
        attachments: draftAttachments,
        editedAt: serverTimestamp(),
        editedByUid: user.uid,
        updatedAt: serverTimestamp(),
      });

      await reload();
      alert("Published post updated.");
    } catch (e: any) {
      console.error("[NewsPage] savePublishedEdit failed", e);
      const msg = getCallableErrorMessage(e);
      setError(msg);
      alert(`Save failed:\n${msg}`);
    }
  }

  async function ensureDraftIdForAttachments(): Promise<string | null> {
    if (editingId) return editingId;

    let expLocal = expiresAtLocal;
    if (!expLocal) {
      const defaultExpIso = isoNowPlusDays(defaultExpireDays);
      expLocal = isoToDatetimeLocal(defaultExpIso);
      setExpiresAtLocal(expLocal);
    }

    const expiresAtIso = datetimeLocalToIso(expLocal);
    if (!expiresAtIso) {
      alert("Expires At is invalid.");
      return null;
    }

    const scheduledPublishAtIso = scheduledPublishAtLocal
      ? datetimeLocalToIso(scheduledPublishAtLocal)
      : null;

    try {
      setUploading(true);
      const res: any = await upsertDraft({
        id: undefined,
        title,
        summary,
        body,
        schoolYear: schoolYear || activeSchoolYear || undefined,
        pushRequested,
        pinnedRequested,
        urgentRequested,
        scheduledPublishAtIso,
        expiresAtIso,
      });

      const id = res?.data?.id;
      if (!id) throw new Error("No draft id returned.");
      setEditingId(String(id));
      setEditingStatus("draft");

      await updateDoc(doc(db, "newsItems", String(id)), {
        attachments: [],
        updatedAt: serverTimestamp(),
      });

      await reload();
      return String(id);
    } catch (e: any) {
      console.error("[NewsPage] ensureDraftIdForAttachments failed", e);
      const msg = getCallableErrorMessage(e);
      setError(msg);
      alert(`Could not create draft for attachments:\n${msg}`);
      return null;
    } finally {
      setUploading(false);
    }
  }

  async function submitForApproval(id: string) {
    setError(null);
    try {
      await submitItem({ id });
      await reload();
      alert("Submitted for approval.");
    } catch (e: any) {
      console.error("[NewsPage] submit failed", e);
      const msg = getCallableErrorMessage(e);
      setError(msg);
      alert(`Submit failed:\n${msg}`);
    }
  }

  async function doApprove(id: string) {
    setError(null);
    try {
      const expiresAtIso = approveExpiresLocal ? datetimeLocalToIso(approveExpiresLocal) : null;
      const scheduledPublishAtIso = approveScheduleLocal ? datetimeLocalToIso(approveScheduleLocal) : null;

      await approveItem({
        id,
        pushApproved: approvePush,
        pinned: approvePinned,
        urgent: approveUrgent,
        scheduledPublishAtIso: scheduledPublishAtIso || null,
        expiresAtIso: expiresAtIso || null,
      });

      await reload();
      alert(scheduledPublishAtIso ? "Approved & scheduled." : "Approved & published.");
    } catch (e: any) {
      console.error("[NewsPage] approve failed", e);
      const msg = getCallableErrorMessage(e);
      setError(msg);
      alert(`Approve failed:\n${msg}`);
    }
  }

  async function doReject(id: string) {
    setError(null);
    const reason = prompt("Rejection reason (required):", "Needs changes") ?? "";
    if (!reason.trim()) return;

    try {
      await rejectItem({ id, reason: reason.trim() });
      await reload();
      alert("Rejected.");
    } catch (e: any) {
      console.error("[NewsPage] reject failed", e);
      const msg = getCallableErrorMessage(e);
      setError(msg);
      alert(`Reject failed:\n${msg}`);
    }
  }

  async function doArchive(id: string) {
    if (!confirm("Unpublish / archive this news item?")) return;
    setError(null);
    try {
      await archiveItem({ id });
      await reload();
      alert("Unpublished / archived.");
    } catch (e: any) {
      console.error("[NewsPage] archive failed", e);
      const msg = getCallableErrorMessage(e);
      setError(msg);
      alert(`Archive failed:\n${msg}`);
    }
  }

  async function softDelete(item: NewsDoc) {
    if (!user) return;

    const label =
      item.status === "published"
        ? "This will remove the post from the app, but it will be retained for compliance. Continue?"
        : "Delete this item (soft delete)?";
    if (!confirm(label)) return;

    setError(null);
    try {
      await updateDoc(doc(db, "newsItems", item.id), {
        status: "deleted",
        deletedAt: serverTimestamp(),
        deletedByUid: user.uid,
        updatedAt: serverTimestamp(),
      });

      if (editingId === item.id) resetEditor();
      await reload();
      alert("Deleted (soft).");
    } catch (e: any) {
      console.error("[NewsPage] softDelete failed", e);
      const msg = getCallableErrorMessage(e);
      setError(msg);
      alert(`Delete failed:\n${msg}`);
    }
  }

  async function restore(item: NewsDoc) {
    if (!(isAdmin || isSuperAdmin)) return;
    if (!confirm("Restore this deleted item?")) return;

    setError(null);
    try {
      const restoredStatus = toMillisSafe(item.publishedAt) ? "published" : "draft";

      await updateDoc(doc(db, "newsItems", item.id), {
        status: restoredStatus,
        deletedAt: null,
        deletedByUid: null,
        updatedAt: serverTimestamp(),
      });

      await reload();
      alert(`Restored as ${restoredStatus}.`);
    } catch (e: any) {
      console.error("[NewsPage] restore failed", e);
      const msg = getCallableErrorMessage(e);
      setError(msg);
      alert(`Restore failed:\n${msg}`);
    }
  }

  async function uploadAttachment(files: FileList | null) {
    if (!files || !files.length) return;
    if (!user) return;

    const targetId = await ensureDraftIdForAttachments();
    if (!targetId) return;

    const allowed = attachmentPolicy.allowedContentTypes;
    const maxBytes = attachmentPolicy.maxBytesNews;
    const maxFiles = attachmentPolicy.maxFilesPerNewsPost;

    const current = Array.isArray(draftAttachments) ? draftAttachments : [];
    if (current.length + files.length > maxFiles) {
      alert(`Max ${maxFiles} attachments per post.`);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      let nextAttachments = [...current];

      for (const file of Array.from(files)) {
        if (!allowed.includes(file.type)) {
          alert(`Not allowed: ${file.name} (${file.type || "unknown"})`);
          continue;
        }
        if (file.size > maxBytes) {
          alert(`Too large: ${file.name} (${Math.round(file.size / 1024 / 1024)}MB). Max ${(maxBytes / 1024 / 1024).toFixed(1)}MB.`);
          continue;
        }

        const attId = makeId();
        const safeName = file.name.replace(/[^\w.\-() ]+/g, "_");
        const storagePath = `news/${targetId}/${attId}-${safeName}`;

        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file, { contentType: file.type });

        const url = await getDownloadURL(storageRef);

        const att: Attachment = {
          id: attId,
          name: file.name,
          size: file.size,
          contentType: file.type,
          storagePath,
          downloadUrl: url,
          createdAtIso: new Date().toISOString(),
        };

        nextAttachments = [...nextAttachments, att];
        setDraftAttachments(nextAttachments);

        await updateDoc(doc(db, "newsItems", targetId), {
          attachments: nextAttachments,
          updatedAt: serverTimestamp(),
        });
      }

      await reload();
    } catch (e: any) {
      console.error("[NewsPage] upload failed", e);
      const msg = getCallableErrorMessage(e);
      setError(msg);
      alert(`Upload failed:\n${msg}`);
    } finally {
      setUploading(false);
    }
  }

  async function removeAttachment(att: Attachment) {
    const targetId = editingId;
    if (!targetId) return;
    if (!confirm(`Remove attachment "${att.name}"?`)) return;

    setError(null);
    setUploading(true);
    try {
      try {
        await deleteObject(ref(storage, att.storagePath));
      } catch {
        // ignore
      }

      const next = draftAttachments.filter((a) => a.id !== att.id);
      setDraftAttachments(next);

      await updateDoc(doc(db, "newsItems", targetId), {
        attachments: next,
        updatedAt: serverTimestamp(),
      });

      await reload();
    } catch (e: any) {
      console.error("[NewsPage] removeAttachment failed", e);
      const msg = getCallableErrorMessage(e);
      setError(msg);
      alert(`Remove failed:\n${msg}`);
    } finally {
      setUploading(false);
    }
  }

  async function copyFacebookPost(item: NewsDoc) {
    const text = facebookReadyCopy(item);
    try {
      await navigator.clipboard.writeText(text);
      alert("Facebook-ready post copied.");
    } catch {
      window.prompt("Copy this Facebook-ready post:", text);
    }
  }

  function renderItemCard(it: NewsDoc, opts?: { adminView?: boolean; pendingView?: boolean }) {
    const adminView = opts?.adminView === true;
    const pendingView = opts?.pendingView === true;
    const isMine = it.authorUid === user?.uid;

    return (
      <div
        key={it.id}
        style={{
          border: "1px solid rgba(0,0,0,0.1)",
          borderRadius: 10,
          padding: 10,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
          <div>
            <b>{it.title}</b> <span className="muted">({it.status})</span>

            {!isMine && adminView ? (
              <span className="muted" style={{ marginLeft: 8 }}>
                • another user’s post
              </span>
            ) : null}

            {it.pinned ? <span className="muted" style={{ marginLeft: 8 }}>📌 pinned</span> : null}
            {it.urgent ? <span className="muted" style={{ marginLeft: 8 }}>⚠ urgent</span> : null}
            {Array.isArray(it.attachments) && it.attachments.length ? (
              <span className="muted" style={{ marginLeft: 8 }}>📎 {it.attachments.length}</span>
            ) : null}

            {it.rejectionReason ? <div className="muted">Reason: {it.rejectionReason}</div> : null}

            {it.status === "deleted" ? (
              <div className="muted">
                Deleted: {toMillisSafe(it.deletedAt) ? new Date(toMillisSafe(it.deletedAt)).toLocaleString() : ""}
              </div>
            ) : null}
          </div>

          <div className="muted">
            {toMillisSafe(it.updatedAt) ? new Date(toMillisSafe(it.updatedAt)).toLocaleString() : ""}
          </div>
        </div>

        <div className="muted" style={{ marginTop: 6 }}>
          {it.summary || ""}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {(it.status === "draft" || it.status === "rejected") && (
            <button className="btn secondary" onClick={() => beginEdit(it)}>
              Edit
            </button>
          )}

          {it.status === "published" && (
            <button className="btn secondary" onClick={() => beginEdit(it)}>
              Edit Published
            </button>
          )}

          {!pendingView && (
            <button className="btn secondary" onClick={() => duplicateToDraft(it)}>
              Duplicate → Draft
            </button>
          )}

          {it.status === "published" ? (
            <>
              <button className="btn secondary" onClick={() => copyFacebookPost(it)}>
                Copy Facebook Post
              </button>
              <a className="btn secondary" href="https://www.facebook.com/" target="_blank" rel="noreferrer">
                Open Facebook
              </a>
            </>
          ) : null}

          {(it.status === "draft" || it.status === "rejected") && isMine && (
            <button className="btn" onClick={() => submitForApproval(it.id)}>
              Submit
            </button>
          )}

          {(isAdmin || isSuperAdmin) && it.status === "published" && (
            <button className="btn secondary" onClick={() => doArchive(it.id)}>
              Unpublish
            </button>
          )}

          {it.status === "deleted" ? (
            (isAdmin || isSuperAdmin) ? (
              <button className="btn secondary" onClick={() => restore(it)}>
                Restore
              </button>
            ) : null
          ) : (
            <button className="btn secondary" onClick={() => softDelete(it)}>
              Delete
            </button>
          )}

          {pendingView && (isAdmin || isSuperAdmin) && (
            <>
              <button className="btn" onClick={() => doApprove(it.id)}>
                Approve
              </button>
              <button className="btn secondary" onClick={() => doReject(it.id)}>
                Reject
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="card">
        <h2>News</h2>
        <div className="muted">Sign in to manage news.</div>
      </div>
    );
  }

  if (!isStaff) {
    return (
      <div className="card">
        <h2>News</h2>
        <div className="muted">You do not have staff access.</div>
      </div>
    );
  }

  const canEditPublished =
    !!editingId &&
    editingStatus === "published" &&
    (isAdmin ||
      isSuperAdmin ||
      (user && myItems.some((x) => x.id === editingId && x.authorUid === user.uid)));

  const nextSchoolYear = activeSchoolYear ? adjacentSchoolYear(activeSchoolYear, 1) : "";
  const schoolYearOptions = Array.from(
    new Set([activeSchoolYear, previousSchoolYear, nextSchoolYear, schoolYear].filter(Boolean))
  );

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="card">
        <h2>News</h2>
        <p className="muted">
          Staff create drafts → submit → Admin approves/publishes. Push / pinned / urgent require admin approval.
          Urgent news becomes the public website homepage banner first; pinned news becomes the banner when there is no urgent item.
          Expires At is required (default {defaultExpireDays} days).
        </p>

        {(isAdmin || isSuperAdmin) ? (
          <>
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
              <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
              Show deleted items (admin)
            </label>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
              <button
                className={adminTab === "mine" ? "btn" : "btn secondary"}
                onClick={() => setAdminTab("mine")}
              >
                My Items
              </button>
              <button
                className={adminTab === "all" ? "btn" : "btn secondary"}
                onClick={() => setAdminTab("all")}
              >
                All News
              </button>
              <button
                className={adminTab === "pending" ? "btn" : "btn secondary"}
                onClick={() => setAdminTab("pending")}
              >
                Pending Approval
              </button>
            </div>
          </>
        ) : null}
      </div>

      {error && (
        <div className="card" style={{ border: "1px solid rgba(220, 38, 38, 0.4)" }}>
          <b style={{ color: "#dc2626" }}>Error</b>
          <div className="muted" style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>
            {error}
          </div>
        </div>
      )}

      {(!isAdmin && !isSuperAdmin) || adminTab === "mine" ? (
        <div className="card">
          <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between" }}>
            <h3>My Items</h3>
            <button className="btn" onClick={beginNew}>
              New Draft
            </button>
          </div>

          <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
            {myItems.map((it) => renderItemCard(it))}
            {!myItems.length && <div className="muted">No items yet.</div>}
          </div>
        </div>
      ) : null}

      {(isAdmin || isSuperAdmin) && adminTab === "all" ? (
        <div className="card">
          <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between" }}>
            <h3>All News</h3>
            <button className="btn" onClick={beginNew}>
              New Draft
            </button>
          </div>

          <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
            {allItems.map((it) => renderItemCard(it, { adminView: true }))}
            {!allItems.length && <div className="muted">No news items.</div>}
          </div>
        </div>
      ) : null}

      <div className="card">
        <h3>
          {editingId
            ? (editingStatus === "published" ? "Edit Published Post" : "Edit Draft")
            : "Create Draft"}
        </h3>

        <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
          <input className="input" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />

          <input
            className="input"
            placeholder="Summary (optional)"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
          />

          <textarea
            className="input"
            placeholder="Body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
          />

          <label className="muted" style={{ display: "grid", gap: 6 }}>
            School Year
            {schoolYearOptions.length ? (
              <select className="input" value={schoolYear} onChange={(e) => setSchoolYear(e.target.value)}>
                {schoolYearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="input"
                placeholder="2026-2027"
                value={schoolYear}
                onChange={(e) => setSchoolYear(e.target.value)}
              />
            )}
          </label>

          {editingStatus !== "published" ? (
            <>
              <div style={{ display: "grid", gap: 10 }}>
                <label className="muted" style={{ display: "grid", gap: 6 }}>
                  Scheduled Publish (optional)
                  <input
                    className="input"
                    type="datetime-local"
                    value={scheduledPublishAtLocal}
                    onChange={(e) => setScheduledPublishAtLocal(e.target.value)}
                  />
                </label>

                <label className="muted" style={{ display: "grid", gap: 6 }}>
                  Expires At (required)
                  <input
                    className="input"
                    type="datetime-local"
                    value={expiresAtLocal}
                    onChange={(e) => setExpiresAtLocal(e.target.value)}
                    onFocus={() => applyDefaultExpiryIfMissing()}
                  />
                </label>
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={pushRequested} onChange={(e) => setPushRequested(e.target.checked)} />
                Request push notification (admin must approve)
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={pinnedRequested} onChange={(e) => setPinnedRequested(e.target.checked)} />
                Request pinned (admin must approve; may show on public homepage banner)
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={urgentRequested} onChange={(e) => setUrgentRequested(e.target.checked)} />
                Request urgent (admin must approve; shows on public homepage banner and auto-expires)
              </label>
            </>
          ) : (
            <div className="muted">
              Editing a published post: content + attachments only. (Scheduling/expiry/approval flags aren’t changed here.)
            </div>
          )}

          <div style={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 10, padding: 10 }}>
            <b>Attachments</b>
            <div className="muted" style={{ marginTop: 6 }}>
              Allowed: configured attachment policy types. Common examples: JPG/PNG/WEBP/PDF/DOCX. You can attach immediately — we will auto-create the draft if needed. Max {attachmentPolicy.maxFilesPerNewsPost} files, {(attachmentPolicy.maxBytesNews / 1024 / 1024).toFixed(1)} MB each.
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
              <input
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.webp,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => uploadAttachment(e.target.files)}
                disabled={uploading}
              />
              {uploading ? <span className="muted">Uploading…</span> : null}
              {!editingId ? <span className="muted">Draft will be created automatically on first upload.</span> : null}
            </div>

            <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
              {draftAttachments.map((a) => (
                <div
                  key={a.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    alignItems: "center",
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderRadius: 10,
                    padding: 10,
                  }}
                >
                  <div style={{ display: "grid" }}>
                    <span>
                      <b>{a.name}</b>{" "}
                      <span className="muted">
                        ({Math.round(a.size / 1024)} KB, {a.contentType})
                      </span>
                    </span>
                    {a.downloadUrl ? (
                      <a className="muted" href={a.downloadUrl} target="_blank" rel="noreferrer">
                        Open
                      </a>
                    ) : null}
                  </div>

                  <button className="btn secondary" onClick={() => removeAttachment(a)} disabled={uploading}>
                    Remove
                  </button>
                </div>
              ))}
              {!draftAttachments.length ? <div className="muted">No attachments yet.</div> : null}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {editingStatus === "published" ? (
              <button className="btn" onClick={savePublishedEdit} disabled={uploading || !editingId || !canEditPublished}>
                Save Changes
              </button>
            ) : (
              <button className="btn" onClick={() => saveDraft()} disabled={uploading}>
                Save Draft
              </button>
            )}

            <button className="btn secondary" onClick={resetEditor} disabled={uploading}>
              Clear
            </button>
          </div>
        </div>
      </div>

      {(isAdmin || isSuperAdmin) && adminTab === "pending" && (
        <div className="card">
          <h3>Pending Approval</h3>

          <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={approvePush} onChange={(e) => setApprovePush(e.target.checked)} />
              Approve push on publish
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={approvePinned} onChange={(e) => setApprovePinned(e.target.checked)} />
              Approve pinned homepage banner
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={approveUrgent} onChange={(e) => setApproveUrgent(e.target.checked)} />
              Approve urgent homepage banner (auto-expires)
            </label>

            <label className="muted" style={{ display: "grid", gap: 6 }}>
              Schedule publish (optional)
              <input
                className="input"
                type="datetime-local"
                value={approveScheduleLocal}
                onChange={(e) => setApproveScheduleLocal(e.target.value)}
              />
            </label>

            <label className="muted" style={{ display: "grid", gap: 6 }}>
              Override expiresAt (optional)
              <input
                className="input"
                type="datetime-local"
                value={approveExpiresLocal}
                onChange={(e) => setApproveExpiresLocal(e.target.value)}
              />
            </label>
          </div>

          <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
            {pending.map((it) => renderItemCard(it, { adminView: true, pendingView: true }))}
            {!pending.length && <div className="muted">No pending items.</div>}
          </div>
        </div>
      )}

      {loading && <div className="muted">Loading…</div>}
    </div>
  );
}
